import { useState } from 'react'

import type { SimilarityMatch } from '../services/analyticsService'

import {
  ConfigurationModal,
  MergeConfirmationModal,
  TagPreviewModal,
} from './modals'
import { useSimilarityAnalysis, useTagPreview } from './hooks'

interface SimilarityAnalysisProps {
  className?: string
}

interface MergeCandidate {
  tag1: string
  tag2: string
  count1: number
  count2: number
}

export function SimilarityAnalysis({ className }: SimilarityAnalysisProps) {
  const {
    analysisData,
    isLoading,
    error,
    mergeLoading,
    mergeResult,
    groupedMatches,
    mergeRecommendations,
    handleAnalyze,
    handleMergeConfirm,
    formatSimilarityScore,
    formatDate,
  } = useSimilarityAnalysis()

  const { isPreviewOpen, previewData, openPreview, closePreview } =
    useTagPreview()

  const [showConfigModal, setShowConfigModal] = useState(false)
  const [selectedType, setSelectedType] = useState<string>('all')
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [mergeCandidate, setMergeCandidate] = useState<MergeCandidate | null>(
    null
  )

  const handleMergeRequest = (
    tag1: string,
    tag2: string,
    count1: number,
    count2: number
  ) => {
    setMergeCandidate({ tag1, tag2, count1, count2 })
    setShowMergeModal(true)
  }

  const handleMergeConfirmClick = async (targetTag: string) => {
    if (!mergeCandidate) return

    const sourceTag =
      mergeCandidate.tag1 === targetTag
        ? mergeCandidate.tag2
        : mergeCandidate.tag1

    setShowMergeModal(false)

    await handleMergeConfirm(sourceTag, targetTag, () => {
      setMergeCandidate(null)
    })
  }

  const filteredMatches =
    selectedType === 'all'
      ? analysisData?.matches || []
      : groupedMatches[selectedType] || []

  const getSimilarityTypeColor = (type: string) => {
    switch (type) {
      case 'exact':
        return 'bg-blue-100 text-blue-800'
      case 'phonetic':
        return 'bg-green-100 text-green-800'
      case 'stem':
        return 'bg-purple-100 text-purple-800'
      case 'semantic':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className={`p-6 bg-white rounded-lg shadow-sm ${className}`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Emotional Tag Similarity Analysis
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowConfigModal(true)}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
            disabled={isLoading}
          >
            Configure Thresholds
          </button>
          <button
            onClick={() => handleAnalyze()}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Analyzing...' : 'Run Analysis'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {mergeResult && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-600 font-medium">✓ {mergeResult.message}</p>
          <p className="text-green-600 text-sm">
            {mergeResult.documentsUpdated} documents updated successfully
          </p>
        </div>
      )}

      {mergeLoading && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-600">
            🔄 Merging tags and updating documents...
          </p>
        </div>
      )}

      {analysisData && (
        <div className="space-y-6">
          {/* Overall Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-600">Total Tags</h3>
              <p className="text-2xl font-bold text-gray-900">
                {analysisData.totalTags}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-medium text-blue-600">
                Similar Matches
              </h3>
              <p className="text-2xl font-bold text-blue-900">
                {analysisData.totalMatches}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-sm font-medium text-green-600">
                Merge Candidates
              </h3>
              <p className="text-2xl font-bold text-green-900">
                {mergeRecommendations.length}
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h3 className="text-sm font-medium text-purple-600">
                Analysis Date
              </h3>
              <p className="text-sm font-bold text-purple-900">
                {formatDate(analysisData.analysisDate)}
              </p>
            </div>
          </div>

          {/* Merge Recommendations */}
          {mergeRecommendations.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">
                Merge Recommendations (90%+ Similarity)
              </h3>
              <div className="space-y-2">
                {mergeRecommendations
                  .slice(0, 10)
                  .map((match: SimilarityMatch, index: number) => (
                    <div
                      key={index}
                      className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">
                            &quot;{match.tag1}&quot;
                          </span>
                          <span className="text-gray-500">↔</span>
                          <span className="font-medium">
                            &quot;{match.tag2}&quot;
                          </span>
                          <span
                            className={`px-2 py-1 rounded text-xs ${getSimilarityTypeColor(match.similarityType)}`}
                          >
                            {match.similarityType}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="font-bold text-green-600">
                              {formatSimilarityScore(match.similarityScore)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {match.count1} + {match.count2} ={' '}
                              {match.combinedCount} uses
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              openPreview(
                                match.tag1,
                                match.tag2,
                                match.count1,
                                match.count2
                              )
                            }
                            className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                          >
                            Preview
                          </button>
                          <button
                            onClick={() =>
                              handleMergeRequest(
                                match.tag1,
                                match.tag2,
                                match.count1,
                                match.count2
                              )
                            }
                            disabled={mergeLoading}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            Merge
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                {mergeRecommendations.length > 10 && (
                  <p className="text-sm text-gray-500">
                    ... and {mergeRecommendations.length - 10} more
                    recommendations
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Match Type Filter */}
          <div>
            <div className="flex items-center space-x-4 mb-4">
              <h3 className="text-lg font-semibold">Similarity Matches</h3>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-3 py-1 border rounded"
              >
                <option value="all">
                  All Types ({analysisData.totalMatches})
                </option>
                {Object.entries(groupedMatches).map(
                  ([type, matches]: [string, SimilarityMatch[]]) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)} (
                      {matches.length})
                    </option>
                  )
                )}
              </select>
            </div>

            {/* Matches List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredMatches
                .slice(0, 50)
                .map((match: SimilarityMatch, index: number) => (
                  <div
                    key={index}
                    className="p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">
                          &quot;{match.tag1}&quot;
                        </span>
                        <span className="text-gray-500">↔</span>
                        <span className="font-medium">
                          &quot;{match.tag2}&quot;
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs ${getSimilarityTypeColor(match.similarityType)}`}
                        >
                          {match.algorithm}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">
                          {formatSimilarityScore(match.similarityScore)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {match.count1} + {match.count2} uses
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              {filteredMatches.length > 50 && (
                <p className="text-sm text-gray-500 text-center py-2">
                  ... and {filteredMatches.length - 50} more matches
                </p>
              )}
              {filteredMatches.length === 0 && (
                <p className="text-gray-500 text-center py-4">
                  No matches found for the selected type.
                </p>
              )}
            </div>
          </div>

          {/* Configuration Display */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium mb-2">Analysis Configuration</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Exact Threshold:</span>
                <span className="ml-1 font-medium">
                  {formatSimilarityScore(analysisData.config.exactThreshold)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Phonetic Threshold:</span>
                <span className="ml-1 font-medium">
                  {formatSimilarityScore(analysisData.config.phoneticThreshold)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Stem Threshold:</span>
                <span className="ml-1 font-medium">
                  {formatSimilarityScore(analysisData.config.stemThreshold)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Semantic Threshold:</span>
                <span className="ml-1 font-medium">
                  {formatSimilarityScore(analysisData.config.semanticThreshold)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfigurationModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onSubmit={handleAnalyze}
        currentConfig={analysisData?.config}
      />

      {mergeCandidate && (
        <MergeConfirmationModal
          isOpen={showMergeModal}
          onClose={() => setShowMergeModal(false)}
          onConfirm={handleMergeConfirmClick}
          tag1={mergeCandidate.tag1}
          tag2={mergeCandidate.tag2}
          count1={mergeCandidate.count1}
          count2={mergeCandidate.count2}
        />
      )}

      {previewData && (
        <TagPreviewModal
          isOpen={isPreviewOpen}
          onClose={closePreview}
          tag1={previewData.tag1}
          tag2={previewData.tag2}
          count1={previewData.count1}
          count2={previewData.count2}
        />
      )}
    </div>
  )
}
